import { useId } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import Carousel, { useCarousel } from '_components/Carousel';
import {
  DealTagStyle,
  ProductStripDealsItem,
} from '@adapters/prismic/types/redesignTypes';
import { trackDealsStripClick } from '_utils/GA4/trackDealsStripEvent';
import DealCard, { dealCardHasTag } from './DealCard';
import styles from './ProductStripDeals.module.scss';

export interface ProductStripDealsProps {
  title?: string;
  items?: ProductStripDealsItem[];
  locale?: string;
}

interface ResolvedCard {
  key: string;
  tagStyle?: DealTagStyle;
  tagText?: string;
  imageUrl?: string;
  imageAlt?: string;
  copy: string[];
  /** Empty when the item has no destination yet (upcoming-deal promo tile). */
  href: string;
}

/** A tile is renderable when it has something to show, link or not. */
const hasContent = (card: ResolvedCard): boolean =>
  !!card.href || !!card.imageUrl || card.copy.length > 0;

const buildHref = (raw: string | undefined, locale?: string): string => {
  const value = raw?.trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  const path = value.startsWith('/') ? value : `/${value}`;
  return `/${locale ?? 'en'}${path}`;
};

const resolveCards = (
  items: ProductStripDealsItem[],
  locale?: string
): ResolvedCard[] =>
  items.map((item, index) => {
    const copy = (item.copy ?? [])
      .map((block) => block?.text?.trim())
      .filter((text): text is string => !!text);

    return {
      key: `${item.product_id ?? item.link ?? 'deal'}-${index}`,
      tagStyle: item.tag_style,
      tagText: item.tag_text,
      imageUrl: item.image?.url,
      imageAlt: item.image?.alt || '',
      copy,
      href: buildHref(item.link, locale),
    };
  });

/**
 * True when the strip has at least one renderable tile — mirrors the guards in
 * `ProductStripDeals` so callers can skip wrappers (and the rhythm spacing they
 * own) for strips that would render nothing.
 */
export const hasRenderableDeals = (
  items?: ProductStripDealsItem[],
  locale?: string
): boolean => !!items?.length && resolveCards(items, locale).some(hasContent);

// ─── Inner component (has access to Carousel context) ─────────────────────────

interface ProductStripDealsInnerProps {
  title?: string;
  headingId: string;
  cards: ResolvedCard[];
}

function ProductStripDealsInner({
  title,
  headingId,
  cards,
}: ProductStripDealsInnerProps) {
  const { t } = useTranslation('common');
  const { asPath } = useRouter();
  // One tagged tile forces the fixed tag row on every tile so heights match;
  // with no tags at all the row is dropped so no tile carries dead space.
  const anyTagged = cards.some(dealCardHasTag);
  const {
    state: { isPrevVisible, isNextVisible },
  } = useCarousel();

  // Show arrows only when the track can actually scroll (AC: arrows appear only
  // when the card count exceeds the visible viewport).
  const showArrows = isPrevVisible || isNextVisible;

  return (
    <>
      <div className={styles.header}>
        {title && (
          <h2 className={styles.heading} id={headingId}>
            {title}
          </h2>
        )}
        {showArrows && (
          <div className={styles.buttonGroup}>
            <Carousel.NavPrev
              aria-label={t('productStripDeals.navPrev')}
              className={styles.navButton}
              iconSize={16}
              iconWeight="bold"
            />
            <Carousel.NavNext
              aria-label={t('productStripDeals.navNext')}
              className={styles.navButton}
              iconSize={16}
              iconWeight="bold"
            />
          </div>
        )}
      </div>

      <Carousel.Track className={styles.swiper} spaceBetween={8}>
        {cards.map((card, i) => (
          <Carousel.Slide key={card.key} className={styles.slide}>
            <DealCard
              tagStyle={card.tagStyle}
              tagText={card.tagText}
              imageUrl={card.imageUrl}
              imageAlt={card.imageAlt}
              copy={card.copy}
              href={card.href}
              priority={i < 3}
              reserveTagSpace={anyTagged}
              onClick={
                card.href
                  ? () =>
                      trackDealsStripClick({
                        data: {
                          interactionLocation: asPath,
                          linkUrl: card.href,
                          linkText: card.copy.join(' '),
                          index: i,
                        },
                      })
                  : undefined
              }
            />
          </Carousel.Slide>
        ))}
      </Carousel.Track>
    </>
  );
}

// ─── Public component ─────────────────────────────────────────────────────────

const ProductStripDeals = ({
  title,
  items,
  locale,
}: ProductStripDealsProps) => {
  const headingId = useId();

  if (!items?.length) return null;

  const cards = resolveCards(items, locale).filter(hasContent);
  if (!cards.length) return null;

  return (
    <section
      className={styles.root}
      {...(title ? { 'aria-labelledby': headingId } : {})}
    >
      <div className={styles.container}>
        <Carousel.Provider>
          <ProductStripDealsInner
            title={title}
            headingId={headingId}
            cards={cards}
          />
        </Carousel.Provider>
      </div>
    </section>
  );
};

export default ProductStripDeals;
